// ===========================================================================
// No Recent Visits — service layer.
//
// Tres endpoints GET que mapean 1:1 con 3 stored procedures de DB_MCM_SANTEPLUS
// (esquema clinical). Comparten los mismos 7 filtros opcionales para que tiles,
// conteo y lista reflejen exactamente el mismo conjunto de datos. JWT
// obligatorio (el backend lee employeeId/IP del token para auditoría HIPAA).
//
// Carga inicial recomendada: disparar getNoRecentVisits + getNoRecentVisitsCount
// + getNoRecentVisitsKpis EN PARALELO con los mismos filtros.
//
// Referencia: docs/modulos/NoRecentVisit/ENDPOINTS_NO_RECENT_VISITS.md
// ===========================================================================
import axios from "axios";
import API_BASE_URL from "../../../config/apiConfig";

const API_URL = `${API_BASE_URL}/api/no-recent-visits`;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

// Arma los query params descartando null/undefined/"" para no enviar filtros
// vacíos (el backend interpreta la ausencia como "todos").
const buildParams = (filters = {}) => {
  const params = {};
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== "") params[k] = v;
  });
  return params;
};

export const TIME_BUCKET_ORDER = ["<3 Months", "3 Months", "6 Months", "9 Months", "12+ Months"];

/**
 * Lista paginada de pacientes sin visita reciente (vista principal).
 * Backend: `clinical.SP_GET_NO_RECENT_VISITS`.
 *
 * Todos los filtros son opcionales; si se omiten devuelve el universo completo.
 * Orden por defecto: alfabético por `memberFullName` ASC, luego `cipId` ASC.
 * El SP **no** devuelve el total — usar `getNoRecentVisitsCount` para paginar.
 *
 * @param {Object} [filters]
 * @param {string|null} [filters.search]      - LIKE sobre CIP ID, Member ID o nombre completo
 * @param {string|null} [filters.dob]         - fecha de nacimiento exacta (YYYY-MM-DD)
 * @param {number|null} [filters.locationId]  - ID de la location/medical center
 * @param {string|null} [filters.pcpName]     - LIKE sobre el nombre del PCP interno
 * @param {string|null} [filters.networkName] - coincidencia exacta (ej. "myBlue")
 * @param {string|null} [filters.metalLevel]  - "Bronze" | "Silver" | "Gold" | "Platinum"
 * @param {string|null} [filters.timeBucket]  - "<3 Months" | "3 Months" | "6 Months" | "9 Months" | "12+ Months"
 * @param {number} [filters.page=1]           - 1-based
 * @param {number} [filters.pageSize=50]      - máximo 500 (el backend trunca)
 *
 * Ejemplo de llamada:
 * GET /api/no-recent-visits?networkName=myBlue&timeBucket=12%2B%20Months&page=1&pageSize=10
 *
 * Respuesta exitosa (200):
 * {
 *   "data": [
 *     {
 *       "cipId": "90000383679",
 *       "memberFullName": "AANENSEN, CHRISTIAN ALAN",
 *       "memberType": "ACA",
 *       "memberId": "H7544928002",
 *       "networkName": "myBlue",
 *       "metalLevel": "Bronze",
 *       "dateOfBirth": "1976-04-11T00:00:00",
 *       "age": 49,
 *       "gender": "M",
 *       "pcpEmployeeId": 47,
 *       "pcpName": "PENA SANCHEZ ORLY",
 *       "specialty": "General Practice",
 *       "locationId": 3,
 *       "medicalCenter": "Lake Worth",
 *       "officeLocation": "5230 LAKE WORTH RD, Lake Worth, FL, 33463",
 *       "lastSeenDate": null,
 *       "daysSinceVisit": null,
 *       "timeSinceLastVisit": "12+ Months"
 *     }
 *   ],
 *   "page": 1,
 *   "pageSize": 10
 * }
 *
 * Códigos:
 * - 200 OK            → incluso con 0 resultados (`data: []`)
 * - 400 Bad Request   → parámetros mal formados (ej. `dob` no parseable)
 * - 401 Unauthorized  → token ausente o inválido
 * - 500               → error del stored procedure
 */
export const getNoRecentVisits = async (filters = {}) => {
  try {
    const response = await axios.get(API_URL, {
      headers: authHeaders(),
      params: buildParams(filters),
    });
    const payload = response.data ?? {};
    // El contrato del front es camelCase (`data`). Toleramos el shape PascalCase
    // del SP (`items`) por si el backend aún no normaliza.
    const data = Array.isArray(payload.data)
      ? payload.data
      : Array.isArray(payload.items)
      ? payload.items
      : [];
    return {
      data,
      page: Number(payload.page ?? filters.page ?? 1),
      pageSize: Number(payload.pageSize ?? filters.pageSize ?? 50),
    };
  } catch (error) {
    console.error("Error en getNoRecentVisits:", error);
    throw error;
  }
};

/**
 * Conteo total para la paginación (mismos filtros que la lista, sin page/pageSize).
 * Backend: `clinical.SP_COUNT_NO_RECENT_VISITS`. El front calcula páginas como
 * `Math.ceil(total / pageSize)`. Llamar **solo cuando cambia un filtro**, no en
 * cada cambio de página.
 *
 * @param {Object} [filters] - los mismos 7 filtros que `getNoRecentVisits`
 *
 * Ejemplo de llamada:
 * GET /api/no-recent-visits/count?networkName=myBlue&timeBucket=12%2B%20Months
 *
 * Respuesta exitosa (200):
 * { "total": 6248 }
 *
 * @returns {Promise<number>} total de registros para los filtros dados
 */
export const getNoRecentVisitsCount = async (filters = {}) => {
  try {
    const { page, pageSize, ...rest } = filters;
    const response = await axios.get(`${API_URL}/count`, {
      headers: authHeaders(),
      params: buildParams(rest),
    });
    const payload = response.data ?? {};
    return Number(payload.total ?? payload.NUM_TOTAL ?? 0);
  } catch (error) {
    console.error("Error en getNoRecentVisitsCount:", error);
    throw error;
  }
};

/**
 * KPIs del header (tiles + datos de los donuts). Mismos 7 filtros: sin filtros
 * → totales del módulo; con filtros → los tiles reflejan el subconjunto (clave
 * para que los donuts sean interactivos).
 * Backend: `clinical.SP_GET_NO_RECENT_VISITS_KPIS` (3 result sets combinados).
 *
 * @param {Object} [filters] - los mismos 7 filtros que `getNoRecentVisits`
 *
 * Ejemplo de llamada:
 * GET /api/no-recent-visits/kpis?networkName=myBlue
 *
 * Respuesta exitosa (200):
 * {
 *   "totals": { "totalMembers": 10414, "pcpCount": 18 },
 *   "networks": [
 *     { "networkName": "myBlue", "count": 9766 },
 *     { "networkName": "NWB",    "count": 210  },
 *     { "networkName": "HMO",    "count": 186  }
 *   ],
 *   "timeBuckets": [
 *     { "timeBucket": "<3 Months",  "count": 421  },
 *     { "timeBucket": "3 Months",   "count": 612  },
 *     { "timeBucket": "6 Months",   "count": 893  },
 *     { "timeBucket": "9 Months",   "count": 1240 },
 *     { "timeBucket": "12+ Months", "count": 7248 }
 *   ]
 * }
 *
 * Notas:
 * - `networks` viene ordenado de mayor a menor (dominante primero).
 * - `timeBuckets` viene en orden lógico de severidad (`<3 Months` → `12+ Months`).
 * - `pcpCount` = número de PCPs internos distintos en el conjunto filtrado.
 *
 * @returns {Promise<{totals:{totalMembers:number,pcpCount:number},networks:Array,timeBuckets:Array}>}
 */
export const getNoRecentVisitsKpis = async (filters = {}) => {
  try {
    const { page, pageSize, ...rest } = filters;
    const response = await axios.get(`${API_URL}/kpis`, {
      headers: authHeaders(),
      params: buildParams(rest),
    });
    const payload = response.data ?? {};
    // Normalización defensiva camelCase ⟷ PascalCase del SP.
    const totalsRaw = payload.totals ?? payload.Totals ?? {};
    const networksRaw = payload.networks ?? payload.Networks ?? [];
    const bucketsRaw = payload.timeBuckets ?? payload.TimeBuckets ?? [];

    return {
      totals: {
        totalMembers: Number(totalsRaw.totalMembers ?? totalsRaw.NUM_TOTAL_MEMBERS ?? 0),
        pcpCount: Number(totalsRaw.pcpCount ?? totalsRaw.NUM_PCP_COUNT ?? 0),
      },
      networks: (Array.isArray(networksRaw) ? networksRaw : []).map((n) => ({
        networkName: n.networkName ?? n.VAR_NETWORK_NAME ?? "—",
        count: Number(n.count ?? n.NUM_COUNT ?? 0),
      })),
      timeBuckets: (Array.isArray(bucketsRaw) ? bucketsRaw : []).map((b) => ({
        timeBucket: b.timeBucket ?? b.VAR_TIME_BUCKET ?? "—",
        count: Number(b.count ?? b.NUM_COUNT ?? 0),
      })),
    };
  } catch (error) {
    console.error("Error en getNoRecentVisitsKpis:", error);
    throw error;
  }
};
